import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { 
  LayoutDashboard, 
  Newspaper, 
  Headphones, 
  Users, 
  Settings,
  Plus,
  BarChart,
  Eye,
  Mail,
  Linkedin,
  Globe,
  Edit3,
  Trash2,
  Play,
  Pause,
  Share2,
  Clock,
  Calendar,
  ChevronRight,
  ChevronLeft,
  Image
} from 'lucide-react';
import useTeamMembers, { type TeamMember } from '../hooks/useTeamMembers';
import usePodcasts, { type PodcastWithRelations } from '../hooks/usePodcasts';
import { useCategories } from '../hooks/useCategories';
import { useTags } from '../hooks/useTags';
import { useNews } from '../hooks/useNews';
import type { Categories, Tags } from '../lib/supabase-types';
import type { NewsArticleWithRelations } from '../types/news';
import { ImageBrowser } from '../components/media/ImageBrowser';
import { AudioBrowser } from '../components/media/AudioBrowser';
import DesktopHeader from '../components/cms/DesktopHeader';
import Sidebar from '../components/cms/Sidebar';
import MemberEditModal from '../components/cms/MemberEditModal';
import { PodcastEditModal } from '../components/cms/PodcastEditModal';
import ImageWithDimensions from '../components/cms/ImageWithDimensions';
import { NewsSection } from './cms/sections/NewsSection';
import type { MenuItem, Section } from '../components/cms/types';
import { supabase } from '../lib/supabase';
import { useNavigate } from 'react-router-dom';
import Toast from '../components/shared/Toast';
import { useAdminNotifications } from '../hooks/useAdminNotifications';
import { Link } from 'react-router-dom';
import MobileNavigation from '../components/cms/MobileNavigation';
import { CMSLayout } from '../components/cms/CMSLayout';
import { Dashboard } from '../components/cms/Dashboard';
import { PodcastsSection } from '../components/cms/PodcastsSection';
import { TeamSection } from '../components/cms/TeamSection';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../hooks/useNotifications';
import MediaManager from './cms/MediaManager';
import { storageService } from '../lib/services/storageService';
import { toast } from 'react-hot-toast';

const menuItems: MenuItem[] = [
  { id: 'dashboard', label: 'Dashboard', section: 'dashboard', icon: LayoutDashboard },
  { id: 'news', label: 'Notícias', section: 'news', icon: Newspaper },
  { id: 'podcasts', label: 'Podcasts', section: 'podcasts', icon: Headphones },
  { id: 'team', label: 'Equipa', section: 'team', icon: Users },
  { id: 'media', label: 'Média', section: 'media', icon: Image },
  { id: 'settings', label: 'Definições', section: 'settings', icon: Settings, path: '/admin/settings' }
];

const CMS = () => {
  const [activeSection, setActiveSection] = useState<Section>('dashboard');
  const [members, setMembers] = useState<TeamMember[]>([]);
  const [previousMonthMembers, setPreviousMonthMembers] = useState(0);
  const [previousMonthNewsArticles, setPreviousMonthNewsArticles] = useState(0);
  const [editingMember, setEditingMember] = useState<TeamMember | null>(null);
  const [showImageBrowser, setShowImageBrowser] = useState(false);
  const [showAudioBrowser, setShowAudioBrowser] = useState(false);
  const [isAdminMode, setIsAdminMode] = useState(true);
  const [newsArticles, setNewsArticles] = useState<NewsArticleWithRelations[]>([]);
  const [newsLoading, setNewsLoading] = useState(true);
  const [showToast, setShowToast] = useState(false);
  const [loading, setLoading] = useState(true);
  const [podcasts, setPodcasts] = useState<PodcastWithRelations[]>([]);
  const [loadingSection, setLoadingSection] = useState(false);
  const [sectionError, setSectionError] = useState<string | null>(null);
  const navigate = useNavigate();

  const { loading: teamLoading, error: teamError, fetchMembers, createMember, updateMember, deleteMember } = useTeamMembers();
  const { user } = useAuth();
  const { createNotification } = useNotifications();
  const { createPodcast, updatePodcast, deletePodcast, updatePodcastTags, fetchPodcasts } = usePodcasts();
  const { loading: categoriesLoading, error: categoriesError, categories } = useCategories();
  const { loading: tagsLoading, error: tagsError, tags } = useTags();
  const { fetchArticles } = useNews();

  const [editingPodcast, setEditingPodcast] = useState<PodcastWithRelations | null>(null);
  const [selectedTags, setSelectedTags] = useState<Tags[]>([]);
  const [currentPodcastPage, setCurrentPodcastPage] = useState(1);
  const podcastsPerPage = 6;

  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  const [contentLoaded, setContentLoaded] = useState(false);

  useEffect(() => {
    const initializeData = async () => {
      try {
        // First check authentication
        const { data: { session } } = await supabase.auth.getSession();
        if (!session) {
          navigate('/login');
          return;
        }

        // Then load data
        console.log('Initializing CMS data...');
        setLoading(true);
        await loadSectionData('dashboard');
        
        console.log('Initial data load complete');
      } catch (error) {
        console.error('Error loading initial data:', error);
        toast.error('Erro ao carregar dados iniciais');
      } finally {
        setLoading(false);
        setContentLoaded(true);
      }
    };

    initializeData();
  }, []);

  const loadSectionData = async (section: Section) => {
    try {
      setSectionError(null);
      setLoadingSection(true);

      switch (section) {
        case 'dashboard':
          if (!contentLoaded) {
            await Promise.all([
              loadMembers(),
              loadPodcastData(),
              loadNewsData()
            ]);
          }
          break;
        case 'team':
          await loadMembers();
          break;
        case 'podcasts':
          await loadPodcastData();
          break;
        case 'news':
          await loadNewsData();
          break;
        case 'media':
          // Media section doesn't need data loading
          break;
      }
    } catch (error) {
      console.error('Error loading section data:', error);
      setSectionError(error instanceof Error ? error.message : 'Error loading data');
      toast.error('Erro ao carregar dados da seção');
      toast.error('Erro ao carregar dados');
    } finally {
      setLoadingSection(false);
    }
  };

  useEffect(() => { 
    if (!contentLoaded) return;
    loadSectionData(activeSection);
  }, [activeSection, contentLoaded]);

  const loadPodcastData = async () => {
    try {
      console.log('Loading podcast data...');
      const data = await fetchPodcasts();
      if (data) {
        setPodcasts(data);
        console.log('Podcasts loaded:', data.length);
      }
    } catch (error) {
      console.error('Error loading podcasts:', error);
      toast.error('Erro ao carregar podcasts');
      throw error;
    }
  };

  const loadNewsData = async () => {
    try {
      console.log('Loading news data...');
      const articles = await fetchArticles();
      if (!articles) {
        throw new Error('No articles returned');
      }
      
      const now = new Date();
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1);
      const previousMonthCount = articles.filter(article => {
        const articleDate = new Date(article.published_at || '');
        return articleDate.getMonth() === lastMonth.getMonth() &&
               articleDate.getFullYear() === lastMonth.getFullYear();
      }).length;
      setNewsArticles(articles);
      setPreviousMonthNewsArticles(previousMonthCount);
    } catch (error) {
      console.error('Error loading news data:', error);
      throw error;
    }
  };

  const loadMembers = async () => {
    try {
      console.log('Loading team members...');
      const data = await fetchMembers();
      
      // Calculate previous month's member count
      const now = new Date();
      const lastMonth = new Date(now.getFullYear(), now.getMonth() - 1);
      const previousCount = data?.filter(member => {
        const memberDate = new Date(member.created_at || '');
        return memberDate.getMonth() === lastMonth.getMonth() &&
               memberDate.getFullYear() === lastMonth.getFullYear();
      }).length || 0;
      
      setMembers(data || []);
      setPreviousMonthMembers(previousCount);
    } catch (error) {
      console.error('Erro ao carregar membros da equipa:', error);
      toast.error('Erro ao carregar membros da equipa');
      throw error;
    }
  };

  const handleCreatePodcast = () => {
    const defaultCategory = categories.find(c => c.slug === 'latest') || categories[0];
    
    if (!defaultCategory) {
      console.error('No categories available');
      return;
    }

    setEditingPodcast({
      id: '',
      title: '',
      description: '',
      category_id: defaultCategory.id,
      audio_url: '',
      image_url: '',
      author: '',
      duration: '',
      plays: 0,
      published_at: new Date().toISOString(),
      created_at: new Date().toISOString(),
      updated_at: new Date().toISOString(),
      slug: '',
      category: defaultCategory,
      tags: []
    });
    setSelectedTags([]);
  };

  const handleSavePodcast = async () => {
    if (!editingPodcast) return;

    try {
      let savedPodcast: PodcastWithRelations;

      if (editingPodcast.id) {
        savedPodcast = await updatePodcast(editingPodcast.id, editingPodcast);
        await updatePodcastTags(editingPodcast.id, selectedTags.map(tag => tag.id));

        // Notificação de atualização
        await createNotification({
          title: 'Podcast Atualizado',
          message: `O podcast "${savedPodcast.title}" foi atualizado com sucesso.`,
          type: 'success',
          link: `/podcasts/${savedPodcast.slug}`,
          user_id: user?.id || ''
        });
      } else {
        savedPodcast = await createPodcast(editingPodcast);
        if (savedPodcast.id) {
          await updatePodcastTags(savedPodcast.id, selectedTags.map(tag => tag.id));
        }

        // Notificação de criação
        await createNotification({
          title: 'Novo Podcast',
          message: `O podcast "${savedPodcast.title}" foi criado com sucesso.`,
          type: 'success',
          link: `/podcasts/${savedPodcast.slug}`,
          user_id: user?.id || ''
        });
      }

      // Limpar estados e fechar modal
      setEditingPodcast(null);
      setSelectedTags([]);

      // Mostrar o toast
      setShowToast(true);

      // Aguardar o toast fechar antes de atualizar a lista
      setTimeout(async () => {
        setShowToast(false);
        await loadPodcastData();
      }, 2000);

    } catch (error) {
      console.error('Error saving podcast:', error);
      
      // Notificação de erro
      await createNotification({
        title: 'Erro ao Salvar Podcast',
        message: 'Ocorreu um erro ao tentar salvar o podcast. Por favor, tente novamente.',
        type: 'error',
        user_id: user?.id || ''
      });
    }
  };

  const handleDeletePodcast = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este podcast?')) {
      try {
        const podcastToDelete = podcasts.find(p => p.id === id);
        await deletePodcast(id);

        // Notificação de exclusão
        await createNotification({
          title: 'Podcast Excluído',
          message: `O podcast "${podcastToDelete?.title}" foi excluído com sucesso.`,
          type: 'warning',
          user_id: user?.id || ''
        });
      } catch (error) {
        console.error('Error deleting podcast:', error);

        // Notificação de erro na exclusão
        await createNotification({
          title: 'Erro ao Excluir Podcast',
          message: 'Ocorreu um erro ao tentar excluir o podcast. Por favor, tente novamente.',
          type: 'error',
          user_id: user?.id || ''
        });
      }
    }
  };

  const handleEditPodcast = (podcast: PodcastWithRelations) => {
    setEditingPodcast(podcast);
    setSelectedTags(podcast.tags || []);
  };

  const calculateMemberPercentage = () => {
    if (previousMonthMembers === 0) return 0;
    const currentCount = members?.length || 0;
    const percentageChange = ((currentCount - previousMonthMembers) / previousMonthMembers) * 100;
    return Math.round(percentageChange);
  };

  const calculateNewsPercentage = () => {
    if (previousMonthNewsArticles === 0) return 0;
    const currentCount = newsArticles?.length || 0;
    const percentageChange = ((currentCount - previousMonthNewsArticles) / previousMonthNewsArticles) * 100;
    return Math.round(percentageChange);
  };

  const memberPercentage = calculateMemberPercentage();
  const isPositiveGrowth = memberPercentage >= 0;

  const newsPercentage = calculateNewsPercentage();
  const isNewsPositiveGrowth = newsPercentage >= 0;

  const handleCreateMember = () => {
    setEditingMember({
      id: '',
      name: '',
      role: '',
      description: '',
      image: '',
      email: '',
      linkedin: '',
      website: ''
    });
  };

  const handleSaveMember = async () => {
    if (!editingMember) return;
    setLoadingSection(true);

    try {
      if (editingMember.id === '') {
        const savedMember = await createMember({
          name: editingMember.name,
          role: editingMember.role,
          description: editingMember.description,
          image: editingMember.image,
          email: editingMember.email,
          linkedin: editingMember.linkedin,
          website: editingMember.website
        });

        setMembers(prev => [savedMember, ...prev]); // Update local state
      } else {
        const updatedMember = await updateMember(editingMember.id, editingMember);
        setMembers(prev => prev.map(m => m.id === updatedMember.id ? updatedMember : m));
      }

      // Close modal and show success message
      setEditingMember(null);
      toast.success('Membro guardado com sucesso!');
      
      // Refresh data for all sections
      await loadSectionData(activeSection);

    } catch (error) {
      console.error('Error saving team member:', error); 
      toast.error('Erro ao salvar membro da equipa');
    } finally {
      setLoadingSection(false);
    }
  };

  const handleDeleteMember = async (id: string) => {
    if (window.confirm('Tem certeza que deseja excluir este membro da equipa?')) {
      try {
        const memberToDelete = members.find(m => m.id === id);
        await deleteMember(id);
        await loadMembers();

        // Notificação de exclusão
        await createNotification({
          title: 'Membro Excluído',
          message: `O membro "${memberToDelete?.name}" foi excluído com sucesso.`,
          type: 'warning',
          user_id: user?.id || ''
        });
      } catch (error) {
        console.error('Error deleting team member:', error);

        // Notificação de erro na exclusão
        await createNotification({
          title: 'Erro ao Excluir Membro',
          message: 'Ocorreu um erro ao tentar excluir o membro da equipa. Por favor, tente novamente.',
          type: 'error',
          user_id: user?.id || ''
        });
      }
    }
  };

  // Adicionar função para calcular o total de páginas de podcasts
  const totalPodcastPages = Math.ceil(podcasts.length / podcastsPerPage);

  // Calcular o índice inicial e final dos podcasts para a página atual
  const indexOfLastPodcast = currentPodcastPage * podcastsPerPage;
  const indexOfFirstPodcast = indexOfLastPodcast - podcastsPerPage;
  const currentPodcasts = podcasts.slice(indexOfFirstPodcast, indexOfLastPodcast);

  // Atualizar as funções de navegação de página
  const handleNextPodcastPage = () => {
    if (currentPodcastPage < totalPodcastPages) {
      setCurrentPodcastPage(currentPodcastPage + 1);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  const handlePrevPodcastPage = () => {
    if (currentPodcastPage > 1) {
      setCurrentPodcastPage(currentPodcastPage - 1);
      window.scrollTo({
        top: 0,
        behavior: 'smooth'
      });
    }
  };

  const handlePodcastPageChange = (page: number) => {
    setCurrentPodcastPage(page);
  };

  const handleSectionChange = (section: Section) => {
    const menuItem = menuItems.find(item => item.section === section);
    if (menuItem?.path) {
      navigate(menuItem.path);
      return;
    }
    setActiveSection(section);
    setEditingMember(null);
    setEditingPodcast(null);
    setSelectedTags([]);
    setShowImageBrowser(false);
    setShowAudioBrowser(false);
  };

  // Add effect to handle section data loading
  useEffect(() => {
    const loadData = async () => {
      if (!contentLoaded) return;
      
      setLoadingSection(true);
      try {
        await loadSectionData(activeSection);
      } catch (error) {
        console.error('Error loading section data:', error);
        toast.error('Erro ao carregar dados');
      } finally {
        setLoadingSection(false);
      }
    };

    loadData();
  }, [activeSection, contentLoaded]);

  // Add effect to reload data when returning to dashboard
  useEffect(() => {
    if (activeSection === 'dashboard' && contentLoaded) {
      loadSectionData('dashboard');
    }
  }, [activeSection, contentLoaded]);

  const formatDuration = (duration: string) => {
    // Remove qualquer texto adicional e mantenha apenas os números e ':'
    const timeMatch = duration.match(/(\d+:)?(\d+:\d+|\d+)/);
    if (!timeMatch) return duration;
    
    const timeParts = timeMatch[0].split(':');
    // Se tiver horas, converte para minutos
    if (timeParts.length === 3) {
      const [hours, minutes, seconds] = timeParts;
      const totalMinutes = parseInt(hours) * 60 + parseInt(minutes);
      return `${totalMinutes}:${seconds}`;
    }
    // Se já estiver em minutos:segundos, retorna como está
    return timeMatch[0];
  };

  const renderActiveSection = () => {
    if (loadingSection) {
      return (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary border-t-transparent" />
        </div>
      );
    }

    if (sectionError) {
      return (
        <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
          {sectionError}
        </div>
      );
    }

    switch (activeSection) {
      case 'dashboard':
        return (
          <Dashboard
            podcasts={podcasts || []}
            members={members || []}
            newsArticles={newsArticles || []}
            memberPercentage={memberPercentage}
            newsPercentage={newsPercentage}
            isPositiveGrowth={isPositiveGrowth}
            isNewsPositiveGrowth={isNewsPositiveGrowth}
          />
        );
      case 'news':
        return <NewsSection activeSection={activeSection} />;
      case 'podcasts':
        return (
          <PodcastsSection
            podcasts={podcasts || []}
            isLoading={loadingSection || loading}
            error={sectionError}
            isAdminMode={isAdminMode}
            onCreatePodcast={handleCreatePodcast}
            onEditPodcast={handleEditPodcast}
            onDeletePodcast={handleDeletePodcast}
            onPlayPodcast={() => {}}
            currentPage={currentPodcastPage}
            onPageChange={handlePodcastPageChange}
          />
        );
      case 'team':
        return (
          <TeamSection
            members={members || []}
            isLoading={loadingSection || loading}
            error={sectionError}
            isAdminMode={isAdminMode}
            onCreateMember={handleCreateMember}
            onEditMember={setEditingMember}
            onDeleteMember={handleDeleteMember}
          />
        );
      case 'media':
        return <MediaManager />;
      case 'settings':
        return (
          <div className="min-h-screen">
            <h2 className="text-2xl font-bold mb-8">Settings</h2>
            <p className="text-gray-400">Settings section is under development.</p>
          </div>
        );
      default:
        return null;
    }
  };

  useEffect(() => {
    if (activeSection !== 'podcasts' && editingPodcast) {
      setEditingPodcast(null);
      setSelectedTags([]);
    }
    if (activeSection !== 'team' && editingMember) {
      setEditingMember(null);
    }
    if (activeSection !== 'news' && showImageBrowser) {
      setShowImageBrowser(false);
    }
    if (activeSection !== 'podcasts' && showAudioBrowser) {
      setShowAudioBrowser(false);
    }
  }, [activeSection]);

  const handleImageBrowse = () => {
    console.log('Abrindo galeria de imagens...');
    setShowImageBrowser(true);
  };

  const handleImageBrowserClose = () => {
    console.log('Fechando galeria de imagens...');
    setShowImageBrowser(false);
  };

  const handleImageSelect = (url: string) => {
    console.log('Imagem selecionada:', url);
    if (editingPodcast) {
      setEditingPodcast({ ...editingPodcast, image_url: url });
    }
    if (editingMember) {
      setEditingMember({ ...editingMember, image: url });
    }
    handleImageBrowserClose();
  };

  return (
    <CMSLayout
                menuItems={menuItems}
                activeSection={activeSection}
                onSectionChange={handleSectionChange}
    >
      <div className="relative min-h-[500px]">
        {loadingSection ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="animate-spin rounded-full h-16 w-16 border-4 border-primary border-t-transparent"></div>
          </div>
        ) : sectionError ? (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="bg-destructive/10 text-destructive p-4 rounded-lg">
              {sectionError}
            </div>
          </div>
        ) : (
          renderActiveSection()
        )}
      </div>

      <MemberEditModal
        member={editingMember || null}
        onSave={handleSaveMember}
        onClose={() => setEditingMember(null)}
        onChange={setEditingMember}
        onImageBrowse={() => setShowImageBrowser(true)}
        isOpen={!!editingMember}
      />

      <PodcastEditModal
        podcast={editingPodcast}
        categories={categories}
        tags={tags}
        selectedTags={selectedTags}
        onSave={handleSavePodcast}
        onClose={() => {
          setEditingPodcast(null);
          setSelectedTags([]);
        }}
        onChange={setEditingPodcast}
        onTagsChange={setSelectedTags}
        onImageBrowse={handleImageBrowse}
        onAudioBrowse={() => setShowAudioBrowser(true)}
        setActiveSection={(section) => setActiveSection(section as Section)}
        isOpen={!!editingPodcast}
      />

      {showImageBrowser && (
        <ImageBrowser
          onSelect={handleImageSelect}
          onClose={handleImageBrowserClose}
          isAdmin={true}
        />
      )}

      {showAudioBrowser && (
        <AudioBrowser
          onSelect={(url) => {
            if (editingPodcast) {
              setEditingPodcast({ ...editingPodcast, audio_url: url });
            }
            setShowAudioBrowser(false);
          }}
          onClose={() => setShowAudioBrowser(false)}
        />
      )}

      <AnimatePresence>
        {showToast && (
          <Toast 
            message="Registo guardado com sucesso!" 
            onClose={() => setShowToast(false)}
            duration={2000}
          />
        )}
      </AnimatePresence>
    </CMSLayout>
  );
};

export default CMS